import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { GraphQLError } from 'graphql';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import ApolloClient from 'apollo-client';
import { pathOr } from 'ramda';
import { getLogger } from '../lib/logger-plus';
import { getUserRoles, hasExpired } from '../lib/user-info';
import { getDeviceInfo } from '../lib/device-info';
import createLogoutFunction from './logout';
import { USER_LOGOUT_MUTATION } from '../lib/queries';

const logger = getLogger('auth');

export enum UserRole {
  SUPERVISOR = 'SUPERVISOR',
  RESTRICTED_SUPERVISOR = 'RESTRICTED-SUPERVISOR',
  WRITER = 'WRITER',
  CAGE_USER = 'CAGE-USER',
  COP_USER = 'COP_USER',
  REV_AUDIT_OFFICER = 'REV-AUDIT-OFFICER',
  CS_OFFICER = 'CS-OFFICER',
  CS_ADMIN_OFFICER = 'CS-ADMIN-OFFICER',
  CS_MANAGER = 'CS-MANAGER'
}

export enum OperationId {
  COP_LOGIN = 'COP_LOGIN',
  PLACE_BETS = 'PLACE_BETS',
  KIOSK_MANAGEMENT = 'KIOSK_MANAGEMENT',
  CASH_MANAGEMENT = 'CASH_MANAGEMENT',
  CASH_MANAGEMENT_ACCOUNTS = 'CASH_MANAGEMENT_ACCOUNTS',
  CASH_MANAGEMENT_TRANSACTIONS = 'CASH_MANAGEMENT_TRANSACTIONS',
  CASH_MANAGEMENT_TRANSACTIONS_AML = 'CASH_MANAGEMENT_TRANSACTIONS_AML',
  CASH_MANAGEMENT_TRANSACTIONS_REWARDS_CARDS = 'CASH_MANAGEMENT_TRANSACTIONS_REWARDS_CARDS',
  COP_STANDARD = 'COP_STANDARD',
  COP_PLAYERS = 'COP_PLAYERS',
  COP_PARLAY_CARDS = 'COP_PARLAY_CARDS',
  QUERY_SHEETS = 'QUERY_SHEETS',
  MUTATION_SHEETS = 'MUTATION_SHEETS',
  COP_ESTATE = 'COP_ESTATE',
  COP_DEVICE_CONNECT = 'COP_DEVICE_CONNECT',
  COP_KIOSK_LIMITS = 'COP_KIOSK_LIMITS',
  CASH_ADJUSTMENT = 'MUTATION_CREATE_CASH_ADJUSTMENT_REQUEST',
  CLEAR_KIOSK_TOTALS = 'CLEAR_KIOSK_TOTALS',
  CREATE_ESTATE_VENUE = 'CREATE_ESTATE_VENUE',
  DISPLAY_VOUCHER_ID = 'DISPLAY_VOUCHER_ID',
  QUERY_CASH_METERING = 'QUERY_CASH_METERING',
  MUTATION_CLEAR_CASH_METERING = 'MUTATION_CLEAR_CASH_METERING',
  DEVICE_STATUS = 'DEVICE_STATUS',
  MUTATION_LOCK_VOUCHER = 'MUTATION_LOCK_VOUCHER',
  MUTATION_UNLOCK_VOUCHER = 'MUTATION_UNLOCK_VOUCHER',
  MUTATION_VOID_VOUCHER = 'MUTATION_VOID_VOUCHER',
  MUTATION_CREATE_PLAYER = 'MUTATION_CREATE_PLAYER',
  MUTATION_UPDATE_PLAYER = 'MUTATION_UPDATE_PLAYER',
  MUTATION_OPEN_CONTEST_FOR_REGISTRATION = 'MUTATION_OPEN_CONTEST_FOR_REGISTRATION',
  MUTATION_CLOSE_CONTEST_FOR_REGISTRATION = 'MUTATION_CLOSE_CONTEST_FOR_REGISTRATION',
  MUTATION_ALLOCATE_CONTEST_PRIZES = 'MUTATION_ALLOCATE_CONTEST_PRIZES',
  MUTATION_CLOSE_CONTEST_ROUND_FOR_REGISTRATION = 'MUTATION_CLOSE_CONTEST_ROUND_FOR_REGISTRATION',
  MUTATION_CONFIRM_CONTEST_PRIZES = 'MUTATION_CONFIRM_CONTEST_PRIZES',
  MUTATION_OPEN_CONTEST_ROUND_FOR_REGISTRATION = 'MUTATION_OPEN_CONTEST_ROUND_FOR_REGISTRATION',
  MUTATION_ALLOCATE_CONTEST_ROUND_POINTS = 'MUTATION_ALLOCATE_CONTEST_ROUND_POINTS',
  MUTATION_CONFIRM_CONTEST_ROUND_PRIZES = 'MUTATION_CONFIRM_CONTEST_ROUND_PRIZES',
  QUERY_VOUCHERS = 'QUERY_VOUCHERS',
  MUTATION_DEVICE_CHANGE_CATEGORY = 'MUTATION_DEVICE_CHANGE_CATEGORY',
  MUTATION_DECOMMISSION_DEVICE = 'MUTATION_DECOMMISSION_DEVICE',
  MUTATION_DISABLE_DEVICE = 'MUTATION_DISABLE_DEVICE',
  MUTATION_ENABLE_DEVICE = 'MUTATION_ENABLE_DEVICE',
  MUTATION_REINSTALL_DEVICE = 'MUTATION_REINSTALL_DEVICE',
  MUTATION_RESTART_DEVICE = 'MUTATION_RESTART_DEVICE',
  MUTATION_UNLOCK_DEVICE = 'MUTATION_UNLOCK_DEVICE',
  SELF_AUTHORISE_CREATE_CASH_ADJUSTMENT_REQUEST = 'SELF_AUTHORISE_CREATE_CASH_ADJUSTMENT_REQUEST',
  AUTHORISE_CREATE_TRANSFER_REQUEST = 'AUTHORISE_CREATE_TRANSFER_REQUEST',
  SELF_AUTHORISE_CREATE_TRANSFER_REQUEST = 'SELF_AUTHORISE_CREATE_TRANSFER_REQUEST',
  AUTHORISE_QUERY_BUSINESS_ACTIVITY_SUMMARY = 'AUTHORISE_QUERY_BUSINESS_ACTIVITY_SUMMARY',
  SELF_AUTHORISE_QUERY_BUSINESS_ACTIVITY_SUMMARY = 'SELF_AUTHORISE_QUERY_BUSINESS_ACTIVITY_SUMMARY',
  SELF_AUTHORISE_PLACE_BETS = 'SELF_AUTHORISE_PLACE_BETS',
  QUERY_VOUCHER = 'QUERY_VOUCHER',
  SELF_AUTHORISE_PAYOUT_AUTH_BET = 'SELF_AUTHORISE_PAYOUT_AUTH_BET',
  EDIT_CUSTOMER_FLAGS = 'EDIT_CUSTOMER_FLAGS',
  ADD_REWARD_POINTS = 'ADD_REWARD_POINTS',
  REMOVE_REWARD_POINTS = 'REMOVE_REWARD_POINTS',
  DISPLAY_TICKET_ID = 'DISPLAY_TICKET_ID',
  NAVIGATE_TO_TICKET = 'NAVIGATE_TO_TICKET',
  QUERY_PRODUCT_CONFIG = 'QUERY_PRODUCT_CONFIG',
  QUERY_ALL_EVENTS = 'QUERY_ALL_EVENTS',
  QUERY_DEVICE_CONFIG = 'QUERY_DEVICE_CONFIG',
  DISPLAY_WALLET = 'DISPLAY_WALLET',
  AUTHORISE_WALLET_DEPOSIT = 'AUTHORISE_WALLET_DEPOSIT',
  SELF_AUTHORISE_WALLET_DEPOSIT = 'SELF_AUTHORISE_WALLET_DEPOSIT',
  AUTHORISE_WALLET_WITHDRAW = 'AUTHORISE_WALLET_WITHDRAW',
  SELF_AUTHORISE_WALLET_WITHDRAW = 'SELF_AUTHORISE_WALLET_WITHDRAW',
  QUERY_VENUE_CONFIG = 'QUERY_VENUE_CONFIG',
  QUERY_VENUE_LIMITS = 'QUERY_VENUE_LIMITS',
  QUERY_REMOTE_AUTH_REQUESTS = 'QUERY_REMOTE_AUTH_REQUESTS',
  COP_LIABILITIES = 'COP_LIABILITIES',
  QUERY_LIFETIME_DEVICE_TALLY = 'QUERY_LIFETIME_DEVICE_TALLY',
  QUERY_TRANSACTION_HISTORY = 'QUERY_TRANSACTION_HISTORY',
  QUERY_DEVICE_LIMITS = 'QUERY_DEVICE_LIMITS',
  DOWNLOAD_AML_DATA = 'DOWNLOAD_AML_DATA',
  COP_CONTESTS = 'COP_CONTESTS',
  CREATE_NEXT_CONTEST_ROUND = 'CREATE_NEXT_CONTEST_ROUND',
  QUERY_CONTEST_ROUND_RESULTS = 'QUERY_CONTEST_ROUND_RESULTS',
  QUERY_CONTEST_ROUND_PRIZES = 'QUERY_CONTEST_ROUND_PRIZES',
  QUERY_CONTEST_STANDINGS = 'QUERY_CONTEST_STANDINGS',
  MUTATION_CONTEST_RESET_PICKS = 'MUTATION_CONTEST_RESET_PICKS',
  MUTATION_CONTEST_OVERRIDE_ROUND_POINTS = 'MUTATION_CONTEST_OVERRIDE_ROUND_POINTS',
  MUTATION_CONTEST_MODIFY_ROUND = 'MUTATION_CONTEST_MODIFY_ROUND',
  MUTATION_VOID_CONTEST_ENTRY = 'MUTATION_VOID_CONTEST_ENTRY',
  MUTATION_REACTIVATE_CONTEST_ENTRY = 'MUTATION_REACTIVATE_CONTEST_ENTRY',
  MUTATION_MOVE_CONTEST_ENTRY = 'MUTATION_MOVE_CONTEST_ENTRY',
  VIEW_TRANSACTION_IMAGES = 'VIEW_TRANSACTION_IMAGES',
  ADD_RACING_REWARD_POINTS = 'ADD_RACING_REWARD_POINTS',
  REMOVE_RACING_REWARD_POINTS = 'REMOVE_RACING_REWARD_POINTS',
  PLACE_PARI_MUTUEL_BETS = 'PLACE_PARI_MUTUEL_BETS',
  POS_LOGIN = 'POS_LOGIN',
  SERVICE_MENU_LOGIN = 'SERVICE_MENU_LOGIN'
}

export type AuthoriseOperationId =
  | 'AUTHORISE_CREATE_CASH_ADJUSTMENT_REQUEST'
  | 'AUTHORISE_CREATE_TRANSFER_REQUEST'
  | 'AUTHORISE_MUTATION_PAYOUT_AUTH_BET'
  | 'AUTHORISE_QUERY_BUSINESS_ACTIVITY_SUMMARY'
  | 'AUTHORISE_WALLET_DEPOSIT'
  | 'AUTHORISE_WALLET_WITHDRAW'
  | 'AUTHORISE_PLACE_BETS'
  | 'AUTHORISE_PLACE_BETS_WITH_CREDITS'
  | 'AUTHORISE_CONTEST_PRIZES_PAYOUT'
  | 'AUTHORISE_FREE_BET'
  | 'AUTHORISE_VAULT_BALANCE_CHECK';

export interface IAuth {
  expired: boolean;
  loading: boolean;
  token: string;
  userRoles: [string];
  permissions: string[];
  message: string;
  setToken: (t: string | null) => void;
  setRoles: (r: string[] | null) => void;
  setPermissions: (p: string[] | null) => void;
  setMessage: (m: string | null) => void;
}

export const AuthContext = createContext({});

export const PERMISSION_QUERY = gql`
  query userPermissions($userRoles: [String!]!, $venueId: String) {
    userPermissions(userRoles: $userRoles, venueId: $venueId)
  }
`;

const formatPermissionError = (error?: { graphQLErrors: readonly GraphQLError[] }) =>
  pathOr(null, ['graphQLErrors', 0, 'extensions', 'code'], error) === 'FORBIDDEN'
    ? 'You do not have the correct permissions to access this app.'
    : 'An error occured, please wait a moment and try again.';

export const useProvidePermissions = () => {
  const storedToken = localStorage.getItem('user');
  const deviceInfo = getDeviceInfo();
  const roles = storedToken ? getUserRoles(storedToken) : null;
  const venueId = deviceInfo ? deviceInfo.slotId.venueId : null;
  const [userRoles, setRoles] = useState(roles);
  const [permissions, setPermissions] = useState();
  const [token, setToken] = useState(storedToken);
  const [message, setMessage] = useState<string>();
  const expired = token ? hasExpired(token) : false;

  useEffect(() => {
    token ? localStorage.setItem('user', token) : localStorage.removeItem('user');
  }, [token]);

  // Do not run the query if permissions already present,
  // no roles present, or if an error message is present.
  const skip: boolean = !!permissions || !userRoles || !!message || expired;
  const variables = { userRoles, venueId };
  const { data, error, loading } = useQuery(PERMISSION_QUERY, {
    variables,
    skip
  });

  if (data) {
    setPermissions(data.userPermissions);
  }

  if (error) {
    setMessage(formatPermissionError(error));
  }

  return {
    expired,
    loading,
    token,
    userRoles,
    permissions,
    message,
    setToken,
    setRoles,
    setPermissions,
    setMessage
  };
};

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const permissions = useProvidePermissions();
  return <AuthContext.Provider value={permissions}>{children}</AuthContext.Provider>;
};

export const useAuth = (): IAuth => useContext<any>(AuthContext);

export const useHasPermission = (permission: OperationId): boolean => hasPermission(useAuth(), permission);

export const hasPermission = ({ permissions }: IAuth, permission: OperationId) =>
  Array.isArray(permissions) && permissions.includes(permission);

export const queryPermissionsOfRoles = async (client: ApolloClient<any>, userRoles: string[]) => {
  const { data, errors } = await client.query({
    query: PERMISSION_QUERY,
    variables: { userRoles }
  });

  return errors ? { error: formatPermissionError({ graphQLErrors: errors }) } : { permissions: data.userPermissions };
};

export const useHasUserRole = (userRole: UserRole): boolean => {
  const { userRoles } = useAuth();
  return Array.isArray(userRoles) && userRoles.includes(userRole);
};

export const useAuthChecks = () => {
  const { permissions, userRoles } = useAuth();

  const hasAnyPermissions = (operations: OperationId[]) => {
    return Array.isArray(permissions) && operations.some(operation => permissions.includes(operation));
  };

  const hasPermission = (operation: OperationId) => {
    return Array.isArray(permissions) && permissions.includes(operation);
  };

  const hasUserRole = (userRole: UserRole) => {
    return Array.isArray(userRoles) && userRoles.includes(userRole);
  };

  return { hasAnyPermissions, hasPermission, hasUserRole };
};

export const useFlushPermissions = () => {
  const { setRoles, setMessage, setPermissions, setToken } = useAuth();
  return () => {
    setRoles(null);
    setToken(null);
    setMessage(null);
    setPermissions(null);
  };
};

export const logoutWithClient = async (client: ApolloClient<any>, endSession: boolean) => {
  await client.mutate({
    mutation: USER_LOGOUT_MUTATION,
    variables: { endSession }
  });
};

type ClientArgs = { client: ApolloClient<any>; endSession?: boolean };
type UrlArgs = { graphqlUrl: string; endSession?: boolean };
type LogoutArgs = ClientArgs | UrlArgs;

function isClient(args: ClientArgs | UrlArgs): args is ClientArgs {
  return Boolean((args as ClientArgs)?.client);
}

export const clearUserTokensAndRefresh = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  window.location.assign('/');
};

export const logout = async (args: LogoutArgs) => {
  const endSession = args?.endSession ?? false;
  try {
    if (isClient(args)) {
      await logoutWithClient(args.client, endSession);
    } else {
      const logoutUser = createLogoutFunction(args.graphqlUrl);
      await logoutUser({ endSession });
    }
  } catch (error) {
    logger.error(error, 'Logout failed.');
  } finally {
    clearUserTokensAndRefresh();
  }
};

export const isSelfAuthEnabled = () => getDeviceInfo()?.features?.selfAuth?.enabled ?? true;
